const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function apiHeaders(extra = {}) {
  return {
    'apikey':
      SUPABASE_KEY,

    'Authorization':
      'Bearer ' +
      SUPABASE_KEY,

    ...extra
  };
}


async function readApiResponse(
  response,
  label
) {
  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      label +
        ' failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const text =
    await response.text();

  return text
    ? JSON.parse(text)
    : null;
}


function shuffle(items) {
  const copy =
    items.slice();

  for (
    let i =
      copy.length - 1;
    i > 0;
    i -= 1
  ) {
    const j =
      Math.floor(
        Math.random() *
          (i + 1)
      );

    [
      copy[i],
      copy[j]
    ] = [
      copy[j],
      copy[i]
    ];
  }

  return copy;
}


function getUsableGroups(
  enrolments
) {
  const groups = [];

  const seen =
    new Set();

  (enrolments || [])
    .filter(
      enrolment =>
        enrolment?.active !==
          false &&
        enrolment
          ?.students
          ?.is_master !==
          true
    )
    .forEach(
      enrolment => {
        const stream =
          Number(
            enrolment.stream
          );

        const groupNumber =
          Number(
            enrolment
              .group_number
          );

        if (
          !Number.isFinite(
            stream
          ) ||
          stream < 1 ||
          !Number.isFinite(
            groupNumber
          ) ||
          groupNumber < 1
        ) {
          return;
        }

        const key =
          String(stream) +
          ':' +
          String(
            groupNumber
          );

        if (
          seen.has(key)
        ) {
          return;
        }

        seen.add(key);

        groups.push({
          stream,

          group_number:
            groupNumber
        });
      }
    );

  return groups;
}


export function buildQiAssignments(
  weekId,
  cqSubmissions,
  enrolments
) {
  const groups =
    getUsableGroups(
      enrolments
    );

  if (!groups.length) {
    throw new Error(
      'No students have both a stream and group assignment.'
    );
  }

  const enrolmentById =
    new Map(
      (enrolments || [])
        .map(
          enrolment => [
            String(
              enrolment.id
            ),
            enrolment
          ]
        )
    );

  const criticalQuestions =
    (cqSubmissions || [])
      .filter(
        submission =>
          String(
            submission
              .week_id
          ) ===
            String(
              weekId
            ) &&
          submission
            .question_type ===
            'Critical' &&
          submission
            .instructor_added !==
            true
      );

  if (
    !criticalQuestions
      .length
  ) {
    throw new Error(
      'No Critical Questions are available for this week.'
    );
  }

  const assignments = [];

  const streams = [
    ...new Set(
      groups.map(
        group =>
          group.stream
      )
    )
  ];

  streams.forEach(
    stream => {
      const streamGroups =
        groups.filter(
          group =>
            group.stream ===
            stream
        );

      const streamQuestions =
        criticalQuestions
          .filter(
            submission =>
              Number(
                enrolmentById
                  .get(
                    String(
                      submission
                        .enrolment_id
                    )
                  )
                  ?.stream
              ) ===
              stream
          );

      /*
       * Do not silently create a partial
       * allocation if one stream has no
       * Critical Questions.
       */
      if (
        !streamQuestions
          .length
      ) {
        throw new Error(
          'Stream ' +
            stream +
            ' has groups but no Critical Questions.'
        );
      }

      /*
       * BOTH sides are randomised.
       *
       * This matters when there is a
       * remainder: the lower-allocation
       * group must not systematically be
       * the last numbered group.
       */
      const shuffledGroups =
        shuffle(
          streamGroups
        );

      const shuffledQuestions =
        shuffle(
          streamQuestions
        );

      /*
       * Fewer questions than groups:
       *
       * every group still gets exactly
       * one question. Questions may be
       * reused, but only inside this
       * stream.
       */
      if (
        shuffledQuestions
          .length <
        shuffledGroups
          .length
      ) {
        shuffledGroups
          .forEach(
            (
              group,
              index
            ) => {
              const question =
                shuffledQuestions[
                  index %
                    shuffledQuestions
                      .length
                ];

              assignments.push({
                course_week_id:
                  weekId,

                cq_submission_id:
                  question.id,

                stream,

                group_number:
                  group
                    .group_number
              });
            }
          );

        return;
      }

      /*
       * Otherwise:
       *
       * - use each question no more
       *   than once;
       * - assign in rounds;
       * - maximum 3 per group;
       * - excess questions beyond
       *   3 per group are ignored.
       *
       * Because group order is shuffled,
       * any incomplete final round falls
       * randomly across groups.
       */
      const questionsToUse =
        shuffledQuestions
          .slice(
            0,
            shuffledGroups
              .length * 3
          );

      questionsToUse
        .forEach(
          (
            question,
            index
          ) => {
            const group =
              shuffledGroups[
                index %
                  shuffledGroups
                    .length
              ];

            assignments.push({
              course_week_id:
                weekId,

              cq_submission_id:
                question.id,

              stream,

              group_number:
                group
                  .group_number
            });
          }
        );
    }
  );

  validateQiAssignments({
    weekId,
    assignments,
    cqSubmissions:
      criticalQuestions,
    enrolments
  });

  return assignments;
}


export function validateQiAssignments({
  weekId,
  assignments,
  cqSubmissions,
  enrolments
}) {
  if (
    !Array.isArray(
      assignments
    ) ||
    !assignments.length
  ) {
    throw new Error(
      'No QI assignments could be constructed.'
    );
  }

  const groups =
    getUsableGroups(
      enrolments
    );

  const enrolmentById =
    new Map(
      (enrolments || [])
        .map(
          enrolment => [
            String(
              enrolment.id
            ),
            enrolment
          ]
        )
    );

  const questionById =
    new Map(
      (cqSubmissions || [])
        .filter(
          submission =>
            String(
              submission
                .week_id
            ) ===
              String(
                weekId
              ) &&
            submission
              .question_type ===
              'Critical' &&
            submission
              .instructor_added !==
              true
        )
        .map(
          submission => [
            String(
              submission.id
            ),
            submission
          ]
        )
    );

  const pairKeys =
    new Set();

  assignments.forEach(
    assignment => {
      if (
        String(
          assignment
            .course_week_id
        ) !==
        String(
          weekId
        )
      ) {
        throw new Error(
          'QI validation failed: an assignment belongs to the wrong week.'
        );
      }

      const question =
        questionById.get(
          String(
            assignment
              .cq_submission_id
          )
        );

      if (!question) {
        throw new Error(
          'QI validation failed: an assigned question is not a Critical Question for this week.'
        );
      }

      const sourceStream =
        Number(
          enrolmentById
            .get(
              String(
                question
                  .enrolment_id
              )
            )
            ?.stream
        );

      if (
        sourceStream !==
        Number(
          assignment.stream
        )
      ) {
        throw new Error(
          'QI validation failed: a question was assigned across streams.'
        );
      }

      const pairKey =
        String(
          assignment.stream
        ) +
        ':' +
        String(
          assignment
            .group_number
        ) +
        ':' +
        String(
          assignment
            .cq_submission_id
        );

      if (
        pairKeys.has(
          pairKey
        )
      ) {
        throw new Error(
          'QI validation failed: the same group received the same question twice.'
        );
      }

      pairKeys.add(
        pairKey
      );
    }
  );

  const streams = [
    ...new Set(
      groups.map(
        group =>
          group.stream
      )
    )
  ];

  streams.forEach(
    stream => {
      const streamGroups =
        groups.filter(
          group =>
            group.stream ===
            stream
        );

      const availableQuestions =
        [
          ...questionById
            .values()
        ].filter(
          question =>
            Number(
              enrolmentById
                .get(
                  String(
                    question
                      .enrolment_id
                  )
                )
                ?.stream
            ) ===
            stream
        );

      if (
        !availableQuestions
          .length
      ) {
        throw new Error(
          'QI validation failed: Stream ' +
            stream +
            ' has groups but no Critical Questions.'
        );
      }

      const counts =
        streamGroups.map(
          group =>
            assignments
              .filter(
                assignment =>
                  Number(
                    assignment
                      .stream
                  ) ===
                    stream &&
                  Number(
                    assignment
                      .group_number
                  ) ===
                    Number(
                      group
                        .group_number
                    )
              )
              .length
        );

      if (
        counts.some(
          count =>
            count < 1
        )
      ) {
        throw new Error(
          'QI validation failed: at least one group received no question.'
        );
      }

      if (
        counts.some(
          count =>
            count > 3
        )
      ) {
        throw new Error(
          'QI validation failed: a group received more than three questions.'
        );
      }

      /*
       * Once there are enough unique
       * questions for every group, the
       * allocation must be balanced.
       *
       * Difference between highest and
       * lowest allocation may be at
       * most one.
       */
      if (
        availableQuestions
          .length >=
        streamGroups
          .length
      ) {
        const highest =
          Math.max(
            ...counts
          );

        const lowest =
          Math.min(
            ...counts
          );

        if (
          highest - lowest >
          1
        ) {
          throw new Error(
            'QI validation failed: question counts are not balanced between groups.'
          );
        }
      }
    }
  );

  return true;
}


async function fetchCurrentQiAssignments(
  weekId
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/cq_question_assignments?course_week_id=eq.' +
        encodeURIComponent(
          weekId
        ) +
        '&select=*',
      {
        headers:
          apiHeaders()
      }
    );

  return readApiResponse(
    response,
    'Loading current QI assignments'
  );
}


async function fetchQiWork(
  assignmentIds
) {
  if (
    !assignmentIds.length
  ) {
    return [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/cq_question_improvements?cq_question_assignment_id=in.(' +
        assignmentIds
          .join(',') +
        ')&select=id&limit=1',
      {
        headers:
          apiHeaders()
      }
    );

  return readApiResponse(
    response,
    'Checking existing QI work'
  );
}


async function deleteQiAssignments(
  weekId
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/cq_question_assignments?course_week_id=eq.' +
        encodeURIComponent(
          weekId
        ),
      {
        method:
          'DELETE',

        headers:
          apiHeaders()
      }
    );

  await readApiResponse(
    response,
    'Deleting old QI assignments'
  );
}


async function insertQiAssignments(
  rows
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/cq_question_assignments',
      {
        method:
          'POST',

        headers:
          apiHeaders({
            'Content-Type':
              'application/json',

            'Prefer':
              'return=representation'
          }),

        body:
          JSON.stringify(
            rows
          )
      }
    );

  return readApiResponse(
    response,
    'Creating QI assignments'
  );
}


export async function assignQiQuestionsForWeek({
  weekId,
  cqSubmissions,
  enrolments,
  allowReassign = true
}) {
  const currentRows =
    await fetchCurrentQiAssignments(
      weekId
    );

  const oldRows =
    Array.isArray(
      currentRows
    )
      ? currentRows
      : [];

  if (
    oldRows.length > 0 &&
    !allowReassign
  ) {
    throw new Error(
      'QI questions have already been assigned for this week.'
    );
  }

  /*
   * Existing work protects the existing
   * allocation from reassignment.
   */
  if (
    oldRows.length > 0
  ) {
    const work =
      await fetchQiWork(
        oldRows.map(
          row =>
            row.id
        )
      );

    if (
      Array.isArray(work) &&
      work.length > 0
    ) {
      throw new Error(
        'Questions cannot be reassigned because students have already started Question Improvement work.'
      );
    }
  }

  /*
   * Build AND validate before deleting
   * any existing allocation.
   */
  const rows =
    buildQiAssignments(
      weekId,
      cqSubmissions,
      enrolments
    );

  if (
    oldRows.length > 0
  ) {
    await deleteQiAssignments(
      weekId
    );
  }

  try {
    const created =
      await insertQiAssignments(
        rows
      );

    if (
      !Array.isArray(
        created
      ) ||
      created.length !==
        rows.length
    ) {
      throw new Error(
        'Supabase returned ' +
          (
            Array.isArray(
              created
            )
              ? created.length
              : 0
          ) +
          ' assignment rows; expected ' +
          rows.length +
          '.'
      );
    }

    /*
     * Validate the records returned by
     * Supabase as well.
     */
    validateQiAssignments({
      weekId,

      assignments:
        created,

      cqSubmissions,

      enrolments
    });

    return created;

  } catch (error) {
    /*
     * Reassignment failed after deletion:
     * restore the previous allocation.
     */
    if (
      oldRows.length > 0
    ) {
      const restoreRows =
        oldRows.map(
          row => ({
            course_week_id:
              row
                .course_week_id,

            cq_submission_id:
              row
                .cq_submission_id,

            stream:
              row.stream,

            group_number:
              row
                .group_number
          })
        );

      try {
        await insertQiAssignments(
          restoreRows
        );

      } catch (
        restoreError
      ) {
        console.error(
          'Could not restore previous QI assignments:',
          restoreError
        );
      }
    }

    throw error;
  }
}
